import React, { useRef, useState, useMemo, useEffect } from 'react';
import './Contacts.css';
import Icon from './Icon';
import { scopedKey } from './scope';
import { applyAutoEnrollAssignments } from './assignmentUtils';
import { ownerMatchesContact } from './nameMatch';
import { confirmDiscard } from './confirmDiscard';
import { putScoped } from './store/syncState';
import { api, usingApi } from './api';
import { formatPhone } from './phone';
import ImportHelp from './ImportHelp';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '汉语' },
  { code: 'es', label: 'El español' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{3}-\d{3}-\d{4}$/;
// Format a US phone as nnn-nnn-nnnn while typing: keep up to 10 digits, auto-insert "-".
// ── Heat scoring ─────────────────────────────────────────────────────────────
// A contact's score drives its order (highest first) and the "heat" tint.
//   status base:  active 75 · pending 50 · lead 0
//   + manual boost (slider)  + engagement points (clicks-driven; capped at 25)
const STATUS_BASE  = { active: 75, pending: 50, lead: 0 };
const OPEN_POINTS_MAX = 25;   // engagement (clicks) contributes at most this much to heat
const SCORE_MAX = 100;        // heat is a 0–100 scale (the slider maps to it)
const statusOf = c => (c.status === 'active' || c.status === 'pending' ? c.status : 'lead');
// Engagement points: the recency-decayed CLICK score from the server drives up to
// OPEN_POINTS_MAX of heat. Opens are tracked for context but score 0 (provider prefetch
// fires false opens). Falls back to the legacy per-contact c.opens count for demo data.
function openPtsOf(c, engScore) {
  const base = engScore != null ? engScore : (c.opens || 0);
  return Math.min(Math.round(base), OPEN_POINTS_MAX);
}
function scoreOf(c, engScore) {
  return Math.max(0, Math.min(SCORE_MAX, (STATUS_BASE[statusOf(c)] || 0) + (c.boost || 0) + openPtsOf(c, engScore)));
}

// Some source files pack multiple addresses into one email field. Keep the first.
function firstEmail(value) {
  if (!value) return '';
  return value.split(/[,;\s]+/)[0].trim();
}

function findColIndex(headers, candidates) {
  for (const c of candidates) {
    const idx = headers.findIndex(h => h.toLowerCase().trim() === c.toLowerCase());
    if (idx !== -1) return idx;
  }
  return -1;
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',');
  const colMap = {
    firstName: findColIndex(headers, ['name_first', 'first name', 'firstname', 'first']),
    middle:    findColIndex(headers, ['name_middle', 'middle name', 'middlename', 'middle']),
    lastName:  findColIndex(headers, ['name_last', 'last name', 'lastname', 'last']),
    email:     findColIndex(headers, ['email1', 'email', 'email address', 'e-mail']),
    mobile:    findColIndex(headers, ['telephone_mobile', 'mobile', 'cell', 'cell phone']),
    home:      findColIndex(headers, ['telephone_home', 'home', 'home phone']),
  };
  return lines.slice(1).map(line => {
    const cols = [];
    let cur = '', inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === ',' && !inQuote) { cols.push(cur); cur = ''; }
      else { cur += ch; }
    }
    cols.push(cur);
    return {
      firstName: colMap.firstName >= 0 ? cols[colMap.firstName]?.trim() : '',
      middle:    colMap.middle    >= 0 ? cols[colMap.middle]?.trim()    : '',
      lastName:  colMap.lastName  >= 0 ? cols[colMap.lastName]?.trim()  : '',
      email:     colMap.email     >= 0 ? firstEmail(cols[colMap.email]) : '',
      mobile:    colMap.mobile    >= 0 ? cols[colMap.mobile]?.trim()    : '',
      home:      colMap.home      >= 0 ? cols[colMap.home]?.trim()      : '',
      wechat: '', instagram: '', birthday: '', language: 'en',
    };
  }).filter(r => r.firstName || r.lastName || r.email);
}

function emptyDraft(base = {}) {
  return {
    firstName: base.firstName || '', lastName:  base.lastName  || '',
    email:     base.email     || '', mobile:    base.mobile    || '',
    home:      base.home      || '', wechat:    base.wechat    || '',
    instagram: base.instagram || '', birthday:  base.birthday  || '',
    language:  base.language  || 'en',
  };
}

// Dedupe identity: a contact is "the same" when BOTH its full name and email match
// (case-insensitive). Two people with the same name but different emails are allowed.
function contactKey(c) {
  const name = [c.firstName, c.middle, c.lastName].map(s => (s || '').toLowerCase().trim()).filter(Boolean).join(' ');
  const email = (c.email || '').toLowerCase().trim();
  return `${name}|${email}`;
}

/* ── Detail (Edit) Pane ── */
function DetailPane({ draft, dirty, onChange, onSave, onCancel }) {
  const fields = [
    { label: 'First Name',   key: 'firstName', type: 'text' },
    { label: 'Last Name',    key: 'lastName',  type: 'text' },
    { label: 'Email',        key: 'email',     type: 'email' },
    { label: 'Phone',        key: 'mobile',    type: 'tel', inputMode: 'numeric', maxLength: 12, placeholder: 'nnn-nnn-nnnn' },
    { label: 'WeChat ID',    key: 'wechat',    type: 'text' },
    { label: 'Instagram ID', key: 'instagram', type: 'text' },
    { label: 'Birthday',     key: 'birthday',  type: 'date' },
  ];
  return (
    <tr className="detail-pane-row">
      <td colSpan={5}>
        <div className="detail-pane">
          <div className="detail-pane-grid">
            {fields.map(f => (
              <div className="detail-field" key={f.key}>
                <label className="detail-label">{f.label}</label>
                <input className="detail-input" type={f.type} value={draft[f.key]}
                  inputMode={f.inputMode} maxLength={f.maxLength} placeholder={f.placeholder}
                  onChange={e => onChange(f.key, f.key === 'mobile' ? formatPhone(e.target.value) : e.target.value)} />
              </div>
            ))}
            <div className="detail-field">
              <label className="detail-label">Language</label>
              <select className="detail-select" value={draft.language}
                onChange={e => onChange('language', e.target.value)}>
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <div className="detail-actions">
            {dirty && <span className="assign-unsaved-hint">● Unsaved changes</span>}
            <button className="rt-save-btn" onClick={onSave}>Save</button>
            <button className="contacts-import-cancel-btn" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

/* ── Main Component ── */
function Contacts({ scope, importEnabled = false, onMessageContacts, focusContactId = null, onFocusHandled }) {
  const K_CONTACTS = scopedKey('ira_contacts', scope);
  const K_PROPERTIES = scopedKey('ira_properties', scope);
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile]         = useState(null);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [contacts, setContacts]               = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_CONTACTS) || '[]'); } catch { return []; }
  });
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [langFilter, setLangFilter] = useState('all');   // 'all' | 'en' | 'zh' | 'es'
  // Per-contact email engagement (opens/clicks → recency-decayed score), keyed by
  // contactId string. Feeds the heat score so engaged contacts rise to the top.
  const [engagement, setEngagement] = useState({});
  // Contacts who unsubscribed from automated greetings (keyed by contactId string →
  // ISO date). Automated sends skip them server-side; the badge tells the agent to
  // switch to a personal follow-up. Manual Message Contacts still works.
  const [optOuts, setOptOuts] = useState({});
  const [resubId, setResubId] = useState(null);   // contact id with the inline resubscribe confirm open
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editDirty, setEditDirty] = useState(false);   // unsaved changes in the open edit pane
  const [deleteId, setDeleteId]   = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());   // bulk-select
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [reviewReq, setReviewReq]   = useState(null);   // bulk review-request confirm/result flow
  const [pendingLink, setPendingLink] = useState(null);  // manual-add: { contact, matches } awaiting link confirm
  const [addingContact, setAddingContact] = useState(false);
  const [addDraft, setAddDraft]   = useState(emptyDraft());
  const [addError, setAddError]   = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [pendingDupes, setPendingDupes]   = useState(0);
  const [linkNote, setLinkNote]   = useState('');     // post-import "linked N to properties" note
  const [drag, setDrag]           = useState(null);   // { id, value } while sliding

  // Persist contacts with an explicit, AWAITED, confirmed save (syncState.putScoped) —
  // the same reliable path MLS/org/agent use — not the fire-and-forget mirror that iOS
  // Safari dropped. localStorage still holds the local cache for reads/hydration.
  const contactsHydrated = useRef(true);
  const lastPersisted = useRef(null);   // value already saved by an explicit handler (saveAdd) — don't re-save
  useEffect(() => {
    localStorage.setItem(K_CONTACTS, JSON.stringify(contacts));   // local cache (ira_contacts is no longer auto-mirrored)
    if (contactsHydrated.current) { contactsHydrated.current = false; lastPersisted.current = contacts; return; }   // skip the value we just hydrated
    if (contacts === lastPersisted.current) return;   // already saved by saveAdd's awaited put — avoid a double write
    putScoped(K_CONTACTS, contacts);   // server save for the other mutations: awaited + retried + drives the indicator
  }, [contacts, K_CONTACTS]);

  // Keep auto-enroll assignments in sync with the contact list: every auto-enrolled
  // reminder is materialized onto each (eligible) contact, and auto-stamped assignments
  // are cleared when their reminder is no longer auto-enrolled. Runs after contacts
  // persist, so newly added/imported contacts (and ones whose data just became eligible,
  // e.g. a birthday was added) pick up their auto reminders.
  useEffect(() => { applyAutoEnrollAssignments(scope); }, [contacts, scope]);

  // Arriving from a property's linked-contact click: open that contact's Edit pane
  // and scroll it into view. Clear any active search first so the row isn't filtered
  // out, then signal the parent so the focus request is consumed (one-shot).
  useEffect(() => {
    if (!focusContactId) return;
    const target = contacts.find(c => String(c.id) === String(focusContactId));
    if (target) {
      setSearch('');
      startEdit(target);
      setTimeout(() => {
        const el = document.getElementById(`ct-contact-${target.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
    }
    if (onFocusHandled) onFocusHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusContactId]);


  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result);
      if (parsed.length === 0) {
        setError('No valid contacts found. Make sure the CSV has headers: name_first, name_last, email1, telephone_mobile, telephone_home.');
        return;
      }
      const seen = new Set(contacts.map(contactKey));
      const unique = [];
      let dupes = 0;
      for (const c of parsed) {
        const k = contactKey(c);
        if (seen.has(k)) { dupes++; continue; }
        seen.add(k);
        unique.push(c);
      }
      setPendingFile(file);
      setPendingDupes(dupes);
      // New imports land in Leads (status 'lead', score 0) by default.
      setPendingContacts(unique.map((c, i) => ({ ...c, id: Date.now() + i, status: 'lead', boost: 0, opens: 0 })));
    };
    reader.readAsText(file);
  }

  // Back-fill property links: a listing property may have been imported before its
  // owner existed as a contact, so it has no linkedContacts yet. Using the SAME fuzzy
  // owner⇄contact match as Property import, attach each newly imported contact to any
  // existing listing property whose owner name matches (respecting the 2-link cap).
  // Returns how many (property, contact) links were created. Writes localStorage
  // directly — the Properties screen reads it fresh on mount, mirroring how Property
  // import reads contacts straight from storage.
  function linkImportedContactsToProperties(newContacts) {
    let properties;
    try { properties = JSON.parse(localStorage.getItem(K_PROPERTIES) || '[]'); } catch { return 0; }
    if (!Array.isArray(properties) || !properties.length) return 0;
    let links = 0;
    const next = properties.map(p => {
      if ((p.role || 'listing') !== 'listing') return p;          // owners only meaningful for listings
      const owners = [p.owner1, p.owner2].filter(Boolean);
      if (!owners.length) return p;
      const linked = Array.isArray(p.linkedContacts) ? [...p.linkedContacts] : [];
      let touched = false;
      for (const c of newContacts) {
        if (linked.length >= 2) break;                            // a property links at most 2 contacts
        if (linked.some(d => String(d.id) === String(c.id))) continue;
        if (owners.some(o => ownerMatchesContact(o, c))) {
          linked.push({ id: c.id, name: `${c.firstName} ${c.lastName}`.trim() });
          links++; touched = true;
        }
      }
      return touched ? { ...p, linkedContacts: linked } : p;
    });
    if (links) { localStorage.setItem(K_PROPERTIES, JSON.stringify(next)); putScoped(K_PROPERTIES, next); }   // properties aren't auto-mirrored — save explicitly
    return links;
  }

  // Referential integrity for deletes: strip the deleted contacts from every property's
  // linkedContacts so no property keeps a stale link. Same direct localStorage+putScoped
  // pattern as linkImportedContactsToProperties above (the Properties screen reads
  // storage fresh on mount). Intentionally NOT a load-time sweep: legacy import links
  // can reference names without live contact records, and those must survive.
  function unlinkContactsFromProperties(ids) {
    const idSet = new Set([...ids].map(String));
    let properties;
    try { properties = JSON.parse(localStorage.getItem(K_PROPERTIES) || '[]'); } catch { return; }
    if (!Array.isArray(properties) || !properties.length) return;
    let touched = false;
    const next = properties.map(p => {
      if (!Array.isArray(p.linkedContacts) || !p.linkedContacts.length) return p;
      const kept = p.linkedContacts.filter(d => !idSet.has(String(d.id)));
      if (kept.length === p.linkedContacts.length) return p;
      touched = true;
      return { ...p, linkedContacts: kept };
    });
    if (touched) { localStorage.setItem(K_PROPERTIES, JSON.stringify(next)); putScoped(K_PROPERTIES, next); }   // properties aren't auto-mirrored — save explicitly
  }

  // Listing properties whose owner name fuzzy-matches this contact and aren't already
  // linked to it — the candidates for a manual-add back-link (shown in the confirm prompt).
  function findListingOwnerMatches(contact) {
    let properties;
    try { properties = JSON.parse(localStorage.getItem(K_PROPERTIES) || '[]'); } catch { return []; }
    if (!Array.isArray(properties)) return [];
    return properties.filter(p =>
      (p.role || 'listing') === 'listing'
      && [p.owner1, p.owner2].filter(Boolean).some(o => ownerMatchesContact(o, contact))
      && !(Array.isArray(p.linkedContacts) && p.linkedContacts.some(d => String(d.id) === String(contact.id)))
    );
  }
  function confirmLinkContact() {
    if (pendingLink) linkImportedContactsToProperties([pendingLink.contact]);
    setPendingLink(null);
  }

  function handleConfirm() {
    const links = linkImportedContactsToProperties(pendingContacts);
    setContacts(prev => [...prev, ...pendingContacts]);   // auto-enroll reminders are materialized by the reconcile effect
    setPendingFile(null); setPendingContacts([]); setPendingDupes(0);
    setLinkNote(links ? `🔗 Linked ${links} imported contact${links !== 1 ? 's' : ''} to existing listing propert${links !== 1 ? 'ies' : 'y'} by owner name.` : '');
  }
  function handleCancelImport() { setPendingFile(null); setPendingContacts([]); setPendingDupes(0); setError(''); }

  async function startEdit(contact) {
    // Opening another contact's editor abandons the current draft — confirm first.
    if (!(await confirmDiscard(editDirty))) return;
    setEditingId(contact.id); setEditDraft(emptyDraft(contact)); setEditDirty(false); setDeleteId(null);
  }
  async function cancelEdit() {
    if (!(await confirmDiscard(editDirty))) return;
    setEditingId(null); setEditDraft({}); setEditDirty(false);
  }
  function saveEdit() {
    setContacts(prev => prev.map(c => c.id === editingId ? { ...c, ...editDraft } : c));
    setEditingId(null); setEditDraft({}); setEditDirty(false);
  }

  async function startAdd() {
    if (!(await confirmDiscard(editDirty))) return;
    setAddingContact(true); setAddDraft(emptyDraft()); setAddError(''); setEditingId(null); setEditDirty(false); setDeleteId(null);
  }
  function cancelAdd() { setAddingContact(false); setAddDraft(emptyDraft()); setAddError(''); }
  async function saveAdd() {
    const firstName = (addDraft.firstName || '').trim();
    const email = (addDraft.email || '').trim();
    const phone = (addDraft.mobile || '').trim();
    if (!firstName) { setAddError('First name is required.'); return; }
    if (!email) { setAddError('Email is required.'); return; }
    if (!EMAIL_RE.test(email)) { setAddError('Please enter a valid email address.'); return; }
    if (phone && !PHONE_RE.test(phone)) { setAddError('Phone must be in nnn-nnn-nnnn format.'); return; }
    const key = contactKey(addDraft);
    if (contacts.some(c => contactKey(c) === key)) {
      setAddError('A contact with this name and email already exists.');
      return;
    }
    const newId = Date.now();
    const newContact = { ...addDraft, id: newId, status: 'lead', boost: 0, opens: 0 };
    const next = [...contacts, newContact];
    // Save to the server FIRST and WAIT for confirmation — exactly like the Add Agent
    // form. The contact only "lands" (UI + cache) once the server has it; on failure the
    // form stays open with an error, so nothing silently fails to persist.
    setAddError(''); setAddSaving(true);
    const ok = await putScoped(K_CONTACTS, next);
    setAddSaving(false);
    if (!ok) { setAddError('Could not save the contact — check your connection and try again.'); return; }
    lastPersisted.current = next;                 // already saved → the persist effect won't re-send it
    setContacts(next);                            // commit to UI + local cache
    setAddingContact(false); setAddDraft(emptyDraft());
    // Manual add: unlike bulk import, ASK before linking to a listing sale, since a
    // single new contact could share a name with a different owner (name collision).
    const matches = findListingOwnerMatches(newContact);
    if (matches.length) setPendingLink({ contact: newContact, matches });
  }

  async function requestDelete(id) {
    if (!(await confirmDiscard(editDirty))) return;
    setDeleteId(id); setEditingId(null); setEditDirty(false);
  }
  // The inline confirmation renders in a row below the target — if that row sits at
  // the bottom edge of the screen, nudge the confirmation into view so it's never missed.
  useEffect(() => {
    // setTimeout (not requestAnimationFrame): rAF can stall in hidden/background tabs.
    if (deleteId) setTimeout(() => {
      document.querySelector('.row-delete-notice')?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }, [deleteId]);

  function executeDelete() {
    unlinkContactsFromProperties([deleteId]);
    setContacts(prev => prev.filter(c => c.id !== deleteId));
    setDeleteId(null);
  }

  // ── Heat actions ──
  function setStatus(id, status) {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, status } : c));
  }
  function setBoost(id, boost) {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, boost: Math.round(boost) } : c));
  }
  // The slider shows/sets the total score (0–100). Status base + email opens set the
  // default position; dragging stores the difference as the manual boost.
  // We persist on EVERY input tick so the change always sticks even if the release
  // event is missed (releasing the thumb off-target, a flick on mobile, etc.). To stop
  // the row from re-sorting out from under the cursor mid-drag, `drag` freezes the row
  // order while sliding; release just unfreezes it.
  function onSliderInput(c, val) {
    setBoost(c.id, val - (STATUS_BASE[statusOf(c)] || 0) - openPts(c));
    setDrag({ id: c.id });
  }
  function commitDrag() { setDrag(null); }

  // Load this agent's contact engagement (opens/clicks scores) and key it by contactId.
  useEffect(() => {
    if (!usingApi()) return;
    let alive = true;
    api.get('/api/engagement')
      .then(d => {
        if (!alive) return;
        const m = {}; (d.engagement || []).forEach(e => { m[String(e.contactId)] = e; }); setEngagement(m);
        const o = {}; (d.optOuts || []).forEach(x => { o[String(x.contactId)] = x.createdAt; }); setOptOuts(o);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [scope]);

  // Agent-side resubscribe — only after the inline confirm (the client must have
  // actually asked to receive greetings again).
  async function resubscribe(contactId) {
    try {
      await api.delete(`/api/engagement/optout/${encodeURIComponent(contactId)}`);
      setOptOuts(prev => { const next = { ...prev }; delete next[String(contactId)]; return next; });
    } catch { /* leave the badge; a retry is harmless */ }
    setResubId(null);
  }

  // Component-local scoring wrappers that fold in real engagement.
  const engOf = (c) => engagement[String(c.id)] || null;
  const engScoreOf = (c) => { const e = engOf(c); return e ? e.score : null; };
  const heat = (c) => scoreOf(c, engScoreOf(c));
  const openPts = (c) => openPtsOf(c, engScoreOf(c));

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return contacts.filter(c => {
      // Language filter — contacts without a language are English ('en', the add/edit default).
      if (langFilter !== 'all' && (c.language || 'en') !== langFilter) return false;
      if (!q) return true;
      return `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q);
    });
  }, [contacts, search, langFilter]);

  // One list, sorted hottest-first — Active (100) / Pending (50) naturally rise
  // above Leads (0). Tie-break by name so order is stable.
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const d = heat(b) - heat(a);
    if (d !== 0) return d;
    // Tie-break hot contacts by the raw (uncapped, decayed) engagement score so active
    // clickers keep separating even after their displayed heat pegs at 100.
    const e = (engScoreOf(b) || 0) - (engScoreOf(a) || 0);
    if (e !== 0) return e;
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
  }), [filtered, engagement]);   // eslint-disable-line react-hooks/exhaustive-deps
  // While a slider is being dragged we persist on every tick, which would normally
  // re-sort the list and yank the row out from under the cursor. Freeze the order to
  // the last snapshot taken before the drag began, and unfreeze on release.
  const orderRef = useRef(null);
  const displayed = useMemo(() => {
    if (drag && orderRef.current) {
      const byId = new Map(filtered.map(c => [c.id, c]));
      const frozen = orderRef.current.map(id => byId.get(id)).filter(Boolean);
      const extras = sorted.filter(c => !orderRef.current.includes(c.id)); // newly-added during drag
      return [...frozen, ...extras];
    }
    orderRef.current = sorted.map(c => c.id);
    return sorted;
  }, [sorted, filtered, drag]);
  // Hottest contact anchors the gradient; everything tints relative to it.
  const maxScore = useMemo(() => Math.max(1, ...filtered.map(heat)), [filtered, engagement]);   // eslint-disable-line react-hooks/exhaustive-deps


  // ── Bulk select / delete ──
  function toggleSelect(id) {
    setSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  function toggleSelectAll() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSel = displayed.length > 0 && displayed.every(c => next.has(c.id));
      displayed.forEach(c => allSel ? next.delete(c.id) : next.add(c.id));
      return next;
    });
  }
  function executeBulkDelete() {
    unlinkContactsFromProperties(selectedIds);
    setContacts(prev => prev.filter(c => !selectedIds.has(c.id)));
    setSelectedIds(new Set());
    setBulkDeleteConfirm(false);
    setEditingId(null);
  }
  const allDisplayedSelected = displayed.length > 0 && displayed.every(c => selectedIds.has(c.id));
  const someDisplayedSelected = displayed.some(c => selectedIds.has(c.id));

  // ── Bulk "Request Reviews" ── build targets from the selected contacts, then send
  // one review-request email each. Contacts who already reviewed (or have no email)
  // are skipped server-side. Gated behind a confirm since it emails real people.
  function openReviewRequest() {
    const sel = contacts.filter(c => selectedIds.has(c.id));
    const targets = sel.map(c => ({
      contactId: String(c.id),
      name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      email: (c.email || '').trim(),
    }));
    const withEmail = targets.filter(t => t.email).length;
    setReviewReq({ stage: 'confirm', targets, withEmail });
  }
  async function confirmSendReviews() {
    setReviewReq(r => ({ ...r, stage: 'sending' }));
    try {
      const d = await api.post('/api/reviews/request-bulk', {
        contacts: reviewReq.targets, appOrigin: window.location.origin,
      });
      setReviewReq({ stage: 'done', result: d });
      setSelectedIds(new Set());
    } catch (e) {
      setReviewReq({ stage: 'error', error: e.message || 'Could not send review requests.' });
    }
  }

  // Export the selected contacts to a .csv. Uses the SAME column headers the importer
  // recognizes (name_first / email1 / telephone_mobile …) so the file re-imports cleanly.
  function exportSelected() {
    const sel = contacts.filter(c => selectedIds.has(c.id));
    if (sel.length === 0) return;
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const headers = ['name_first', 'name_middle', 'name_last', 'email1', 'telephone_mobile', 'telephone_home', 'wechat', 'instagram', 'birthday', 'language', 'status'];
    const lines = sel.map(c => [c.firstName, c.middle, c.lastName, c.email, c.mobile, c.home, c.wechat, c.instagram, c.birthday, c.language, statusOf(c)].map(esc).join(','));
    const csv = [headers.join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `contacts-export-${sel.length}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  // A clean rounded "heat badge": deeper red + white text as the score rises,
  // soft red tint + dark text when cool.
  function scoreBadgeStyle(score) {
    const heat = Math.min(1, score / maxScore);    // 0..1
    return {
      background: `rgba(229, 62, 62, ${(0.14 + heat * 0.78).toFixed(3)})`,
      color: heat > 0.45 ? '#fff' : '#7b341e',
      borderColor: `rgba(197, 48, 48, ${(0.18 + heat * 0.4).toFixed(3)})`,
    };
  }

  // Renders all contact rows, hottest first.
  function rows(list) {
    if (list.length === 0) {
      return <tr><td colSpan={6} className="contacts-no-results">No contacts match your search.</td></tr>;
    }
    return list.map(c => {
      const shown = heat(c);   // boost is committed live, so the score is always current
      const e = engOf(c);
      const engTitle = e
        ? `Heat ${shown} · ${e.opens || 0} open${e.opens === 1 ? '' : 's'}, ${e.clicks || 0} click${e.clicks === 1 ? '' : 's'}${e.appreciates > 0 ? `, ${e.appreciates} thank${e.appreciates === 1 ? '-you' : '-yous'}` : ''}${e.lastEventAt ? ` · last ${new Date(e.lastEventAt).toLocaleDateString()}` : ''}`
        : `Heat score ${shown}`;
      const optedOutAt = optOuts[String(c.id)];
      return (
      <React.Fragment key={c.id}>
        <tr
          id={`ct-contact-${c.id}`}
          className={`ct-row ${deleteId === c.id ? 'delete-target-row' : ''} ${editingId === c.id ? 'editing-active-row' : ''} ${selectedIds.has(c.id) ? 'selected-row' : ''}`}
        >
          <td className="col-select">
            <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} />
          </td>
          <td data-label="Heat" className="ct-thermo-cell">
            <span className="ct-score-badge" style={scoreBadgeStyle(shown)} title={engTitle}>{shown}</span>
            {e && (e.clicks > 0 || e.opens > 0) && (
              <span className="ct-eng-dot" title={engTitle}>{e.clicks > 0 ? '🔗' : '📨'}</span>
            )}
            <input className="ct-slider" type="range" min="0" max="100" value={shown}
              title="Drag to set heat score"
              style={{ background: `linear-gradient(to right, #e53e3e ${shown}%, #edf2f7 ${shown}%)` }}
              onChange={e => onSliderInput(c, Number(e.target.value))}
              onPointerUp={commitDrag}
              onPointerCancel={commitDrag}
              onMouseUp={commitDrag}
              onTouchEnd={commitDrag}
              onKeyUp={commitDrag}
              onBlur={commitDrag} />
          </td>
          <td data-label="Contact Name" className="ct-name">
            {`${c.firstName} ${c.lastName}`.trim()}
            {optedOutAt && (
              <button type="button" className="ct-optout-badge"
                title={`Unsubscribed from automated greetings on ${new Date(optedOutAt).toLocaleDateString()}. Reminders skip this contact — a personal message (Message Contacts) still works. Click to resubscribe.`}
                onClick={() => setResubId(resubId === c.id ? null : c.id)}>
                ✋ opted out
              </button>
            )}
          </td>
          <td data-label="Email">{c.email}</td>
          <td data-label="Status">
            <select className={`ct-status ct-status-${statusOf(c)}`} value={statusOf(c)}
              onChange={e => setStatus(c.id, e.target.value)}>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="lead">Lead</option>
            </select>
          </td>
          <td className="col-actions">
            <button className={`icon-btn edit-icon-btn ${editingId === c.id ? 'active' : ''}`} title="Edit contact"
              onClick={() => editingId === c.id ? cancelEdit() : startEdit(c)}><Icon name="rename" size={16} /></button>
            <button className="icon-btn delete-icon-btn" title="Delete contact"
              onClick={() => requestDelete(c.id)}><Icon name="delete" size={16} /></button>
          </td>
        </tr>
        {/* Inline resubscribe confirmation — same right-under-the-row pattern as delete.
            The wording makes the agent own the consent: only resubscribe when the
            client actually asked to receive greetings again. */}
        {resubId === c.id && (
          <tr className="row-delete-confirm">
            <td colSpan={6}>
              <div className="contacts-delete-notice row-delete-notice">
                <span>✋ Resubscribe <strong>{c.firstName} {c.lastName}</strong>? Only do this if they asked to receive your greetings again.</span>
                <button className="contacts-import-confirm-btn" onClick={() => resubscribe(c.id)}>Resubscribe</button>
                <button className="contacts-import-cancel-btn" onClick={() => setResubId(null)}>Cancel</button>
              </div>
            </td>
          </tr>
        )}
        {/* Inline delete confirmation — rendered right under the target row so it's
            always visible where the Delete was clicked (a top-of-list banner scrolls
            out of view on long lists). */}
        {deleteId === c.id && (
          <tr className="row-delete-confirm">
            <td colSpan={6}>
              <div className="contacts-delete-notice row-delete-notice">
                <span><Icon name="delete" size={16} /> Delete <strong>{c.firstName} {c.lastName}</strong>? This cannot be undone.</span>
                <button className="contacts-delete-confirm-btn" onClick={executeDelete}>Delete</button>
                <button className="contacts-import-cancel-btn" onClick={() => setDeleteId(null)}>Cancel</button>
              </div>
            </td>
          </tr>
        )}
        {editingId === c.id && (
          <DetailPane
            draft={editDraft}
            dirty={editDirty}
            onChange={(field, val) => { setEditDraft(prev => ({ ...prev, [field]: val })); setEditDirty(true); }}
            onSave={saveEdit}
            onCancel={cancelEdit}
          />
        )}
      </React.Fragment>
      );
    });
  }

  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <div>
          <h2>Contacts</h2>
          <p>Hottest contacts rise to the top. Fine-tune heat with the slider.</p>
        </div>
        <div className="contacts-header-btns">
          <button className="contacts-add-btn" onClick={startAdd}>
            <Icon name="add" size={16} /><span className="lbl-full">Add Contact</span><span className="lbl-short">Add</span>
          </button>
          {importEnabled && (
            <>
              <button className="contacts-import-btn" onClick={() => fileInputRef.current.click()}>
                <Icon name="import" size={16} /> <span className="lbl-full">Import Contacts</span><span className="lbl-short">Import</span>
              </button>
              <ImportHelp type="contacts" />
            </>
          )}
          {onMessageContacts && (
            <button className="contacts-message-btn" onClick={onMessageContacts}>
              <Icon name="mail" size={16} /> <span className="lbl-full">Message Contacts</span><span className="lbl-short">Message</span>
            </button>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
      </div>

      {!importEnabled && (
        <div className="contacts-mls-hint"><Icon name="lock" size={16} /> Select a Supported MLS in your <strong>Profile</strong> to enable importing contacts.</div>
      )}

      {error && <div className="contacts-error">⚠ {error}</div>}

      {linkNote && (
        <div className="contacts-import-notice">
          <span>{linkNote}</span>
          <button className="contacts-import-cancel-btn" onClick={() => setLinkNote('')}>Dismiss</button>
        </div>
      )}

      {pendingFile && (
        <div className="contacts-import-notice">
          <span><Icon name="files" size={16} /> <strong>{pendingFile.name}</strong> — {pendingContacts.length} contact{pendingContacts.length !== 1 ? 's' : ''} ready to import.
            {pendingDupes > 0 && <em className="contacts-dupe-note"> {pendingDupes} duplicate{pendingDupes !== 1 ? 's' : ''} removed.</em>}
          </span>
          {pendingContacts.length > 0
            ? <button className="contacts-import-confirm-btn" onClick={handleConfirm}>Confirm Import</button>
            : <em className="contacts-dupe-note">Nothing new to import.</em>}
          <button className="contacts-import-cancel-btn" onClick={handleCancelImport}>Cancel</button>
        </div>
      )}

      {addingContact && (
        <div className="add-contact-panel">
          <div className="add-contact-panel-title">New Contact</div>
          {addError && <div className="contacts-error">⚠ {addError}</div>}
          <div className="detail-pane-grid">
            {[
              { label: 'First Name', key: 'firstName', type: 'text', required: true },
              { label: 'Last Name',  key: 'lastName',  type: 'text' },
              { label: 'Email',      key: 'email',     type: 'email', required: true },
              { label: 'Phone',      key: 'mobile',    type: 'tel', inputMode: 'numeric', maxLength: 12, placeholder: 'nnn-nnn-nnnn' },
              { label: 'WeChat ID',  key: 'wechat',    type: 'text' },
              { label: 'Instagram ID', key: 'instagram', type: 'text' },
              { label: 'Birthday',   key: 'birthday',  type: 'date' },
            ].map(f => (
              <div className="detail-field" key={f.key}>
                <label className="detail-label">{f.label}{f.required && <span className="detail-required"> *</span>}</label>
                <input className="detail-input" type={f.type || 'text'} value={addDraft[f.key]}
                  inputMode={f.inputMode} maxLength={f.maxLength} placeholder={f.placeholder}
                  onChange={e => {
                    const v = f.key === 'mobile' ? formatPhone(e.target.value) : e.target.value;
                    setAddDraft(prev => ({ ...prev, [f.key]: v })); setAddError('');
                  }} />
              </div>
            ))}
            <div className="detail-field">
              <label className="detail-label">Language</label>
              <select className="detail-select" value={addDraft.language}
                onChange={e => setAddDraft(prev => ({ ...prev, language: e.target.value }))}>
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <div className="detail-actions">
            <button className="rt-save-btn" onClick={saveAdd} disabled={addSaving}>{addSaving ? 'Saving…' : 'Save'}</button>
            <button className="contacts-import-cancel-btn" onClick={cancelAdd} disabled={addSaving}>Cancel</button>
          </div>
        </div>
      )}

      {bulkDeleteConfirm && (
        <div className="contacts-delete-notice">
          <span><Icon name="delete" size={16} /> Delete <strong>{selectedIds.size}</strong> selected contact{selectedIds.size !== 1 ? 's' : ''}? This cannot be undone.</span>
          <button className="contacts-delete-confirm-btn" onClick={executeBulkDelete}>Delete</button>
          <button className="contacts-import-cancel-btn" onClick={() => setBulkDeleteConfirm(false)}>Cancel</button>
        </div>
      )}

      {pendingLink && (
        <div className="contacts-delete-notice contacts-link-notice">
          <span>
            <strong>{pendingLink.contact.firstName} {pendingLink.contact.lastName}</strong> matches the owner on {pendingLink.matches.length} listing sale{pendingLink.matches.length !== 1 ? 's' : ''}
            {' '}({pendingLink.matches.slice(0, 3).map(p => p.address || p.listingNumber || '—').join('; ')}{pendingLink.matches.length > 3 ? '…' : ''}).
            {' '}Link this contact to {pendingLink.matches.length !== 1 ? 'them' : 'it'}? Skip if this is a different person.
          </span>
          <button className="contacts-link-confirm-btn" onClick={confirmLinkContact}>Link</button>
          <button className="contacts-import-cancel-btn" onClick={() => setPendingLink(null)}>Skip</button>
        </div>
      )}

      {reviewReq && (
        <div className="contacts-delete-notice contacts-review-notice">
          {reviewReq.stage === 'confirm' && (<>
            <span><Icon name="star" size={16} /> Email a review request to <strong>{reviewReq.withEmail}</strong> of {reviewReq.targets.length} selected contact{reviewReq.targets.length !== 1 ? 's' : ''} with an email on file? Anyone who already reviewed their most recent closing is skipped automatically.</span>
            <button className="contacts-delete-confirm-btn contacts-review-send" onClick={confirmSendReviews} disabled={reviewReq.withEmail === 0}>Send requests</button>
            <button className="contacts-import-cancel-btn" onClick={() => setReviewReq(null)}>Cancel</button>
          </>)}
          {reviewReq.stage === 'sending' && <span>Sending review requests…</span>}
          {reviewReq.stage === 'done' && (<>
            <span>✓ Sent <strong>{reviewReq.result.sent}</strong> request{reviewReq.result.sent !== 1 ? 's' : ''}
              {reviewReq.result.skippedAlready ? `, skipped ${reviewReq.result.skippedAlready} who already reviewed` : ''}
              {reviewReq.result.skippedPending ? `, ${reviewReq.result.skippedPending} already had a pending request` : ''}
              {reviewReq.result.skippedNoEmail ? `, ${reviewReq.result.skippedNoEmail} without an email` : ''}
              {reviewReq.result.failed ? `, ${reviewReq.result.failed} failed to send` : ''}.</span>
            <button className="contacts-import-cancel-btn" onClick={() => setReviewReq(null)}>Close</button>
          </>)}
          {reviewReq.stage === 'error' && (<>
            <span>⚠ {reviewReq.error}</span>
            <button className="contacts-import-cancel-btn" onClick={() => setReviewReq(null)}>Close</button>
          </>)}
        </div>
      )}

      {contacts.length > 0 ? (
        <>
          <div className="contacts-toolbar">
            <input className="contacts-search" type="text"
              placeholder="Search by contact name or email…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className="contacts-lang-filter" title="Filter by language"
              value={langFilter} onChange={e => setLangFilter(e.target.value)}>
              <option value="all">All languages</option>
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            {(search || langFilter !== 'all') && (
              <span className="contacts-search-count">
                {filtered.length} of {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Mobile-only select-all + selected count (the desktop count lives in the
              bulk bar). Header checkbox is hidden when the table reflows into cards. */}
          <div className="contacts-mobile-selectall">
            <label className="cms-toggle">
              <input type="checkbox"
                ref={el => { if (el) el.indeterminate = someDisplayedSelected && !allDisplayedSelected; }}
                checked={allDisplayedSelected}
                onChange={toggleSelectAll} />
              <span>{allDisplayedSelected ? 'Deselect all' : 'Select all'}{displayed.length ? ` (${displayed.length})` : ''}</span>
            </label>
            {selectedIds.size > 0 && <span className="cms-count">{selectedIds.size} selected</span>}
          </div>

          {selectedIds.size > 0 && (
            <div className="contacts-bulk-bar">
              <span className="contacts-bulk-count">{selectedIds.size} selected</span>
              <button className="contacts-bulk-review-btn" onClick={openReviewRequest}>
                <Icon name="star" size={16} /> <span className="lbl-full">Request Reviews</span><span className="lbl-short">Review</span>
              </button>
              <button className="contacts-bulk-export-btn" onClick={exportSelected}>
                <Icon name="export" size={16} /> <span className="lbl-full">Export Selected</span><span className="lbl-short">Export</span>
              </button>
              <button className="contacts-bulk-delete-btn" onClick={() => setBulkDeleteConfirm(true)}>
                <Icon name="delete" size={16} /> <span className="lbl-full">Delete Selected</span><span className="lbl-short">Delete</span>
              </button>
            </div>
          )}

          <div className="contacts-table-wrap">
            <table className="contacts-table ct-table">
              <thead>
                <tr>
                  <th className="col-select">
                    <input type="checkbox" title="Select all"
                      ref={el => { if (el) el.indeterminate = someDisplayedSelected && !allDisplayedSelected; }}
                      checked={allDisplayedSelected}
                      onChange={toggleSelectAll} />
                  </th>
                  <th className="ct-th-heat"><Icon name="thermometer" size={16} /> Heat</th>
                  <th>Contact Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>{rows(displayed)}</tbody>
            </table>
          </div>
        </>
      ) : !pendingFile && (
        <div className="contacts-empty">
          <span className="contacts-empty-icon"><Icon name="contacts" size={18} /></span>
          <p>No contacts yet. {importEnabled ? 'Import a CSV file or add one to get started.' : 'Add one to get started.'}</p>
        </div>
      )}
    </div>
  );
}

export default Contacts;
